import React, { useState, useEffect, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import { addDays, format } from 'date-fns';
import PropTypes from 'prop-types';

import classes from '@/components/AssignInspector/AssignInspector.module.scss';
import { orderType as parseOrderType } from '@/constants';
import ApiContext from '@/contexts/ApiContext';
import { TIME_MASK } from '@/helpers/mask';
import scheduleImage from '@/images/icons/calendar-circle.svg';
import InspectorsFilter from '@/inspectionAttach/InspectorsFilter';

const AssignInspector = ({ assignDeviceToOrder, cancelHandler }) => {
  const intl = useIntl();
  const { status } = useContext(ApiContext);
  const [dates, setDates] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [features, setFeatures] = useState(null);

  useEffect(() => {
    const dateOptions = Array(30)
      .fill()
      .map((_, index) => {
        const day = format(addDays(new Date(), index), 'dd/MM/yyyy');
        return { value: day, label: day };
      });
    setDates(dateOptions);

    const userMe = JSON.parse(window.sessionStorage.getItem('me'));
    if (userMe?.features) {
      setFeatures(userMe.features.map(feature => parseOrderType.get(feature)));
    }
  }, []);

  const selectInspectorHandler = () => {
    setErrorMessage(null);
  };

  const assignConfirmedHandler = (evt) => {
    evt.preventDefault();
    const formData = new FormData(evt.currentTarget);
    const startTime = new Date();
    const endTime = new Date();
    
    const startTimeVal = formData.get('start_time');
    const endTimeVal = formData.get('end_time');

    if (!startTimeVal || startTimeVal.length < 5 || !endTimeVal || endTimeVal.length < 5) {
      setErrorMessage(intl.formatMessage({ id: 'MESSAGE.INVALID_DATE' }));
      return;
    }

    const [startHour, startMinute] = startTimeVal.split(':');
    startTime.setHours(parseInt(startHour, 10));
    startTime.setMinutes(parseInt(startMinute, 10));
    
    const [endHour, endMinute] = endTimeVal.split(':');
    endTime.setHours(parseInt(endHour, 10));
    endTime.setMinutes(parseInt(endMinute, 10));

    if (formData.get('inspector')?.length !== 36) {
      setErrorMessage(intl.formatMessage({ id: 'MESSAGE.INSPECTOR_REQUIRED' }));
      return;
    }

    if (formData.get('date')?.length !== 10) {
      setErrorMessage(intl.formatMessage({ id: 'MESSAGE.DATE_REQUIRED' }));
      return;
    }

    if (startTime >= endTime) {
      setErrorMessage(intl.formatMessage({ id: 'MESSAGE.WRONG_DATE' }));
      return;
    }

    // Convert DD/MM/YYYY to YYYY-MM-DD
    const dateStr = formData.get('date');
    const [day, month, year] = dateStr.split('/');
    const formattedDate = `${year}-${month}-${day}`;

    const formValues = {
      inspector: formData.get('inspector'),
      date: formattedDate,
      start_time: startTimeVal,
      end_time: endTimeVal,
      message: formData.get('message'),
    };

    assignDeviceToOrder(formValues);
  };

  const isScheduleDisabled = features?.indexOf('schedule') === -1;

  if (isScheduleDisabled) {
    return (
      <div className={classes.AssignInspector}>
        <div className={classes.Title}>
          <h2>{intl.formatMessage({ id: 'SCHEDULE' })}</h2>
          <h3>{intl.formatMessage({ id: 'ASSIGNMENT' })}</h3>
          <div className={classes.badge}>
            {intl.formatMessage({ id: 'NEW' })}
          </div>
        </div>
        
        <div className={classes.content}>
          <div className={classes.icon}>
            <img src={scheduleImage} alt={intl.formatMessage({ id: 'SCHEDULE' })} />
          </div>
          <div className={classes.text}>
            {intl.formatMessage({ id: 'INSPECTOR_ASSIGN_01_MESSAGE' })}
            <br />
            {intl.formatMessage({ id: 'INSPECTOR_ASSIGN_02_MESSAGE' })}
            <br />
            {intl.formatMessage({ id: 'INSPECTOR_ASSIGN_03_MESSAGE' })}
          </div>
        </div>
        
        <hr />
        
        <div className={classes.actions}>
          <Link to="/schedule">
            {intl.formatMessage({ id: 'GO_TO_SCHEDULE' })}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form id="assignInspectorForm" onSubmit={assignConfirmedHandler}>
      <div className={classes.AssignInspector}>
        <div className={classes.Title}>
          <h2>{intl.formatMessage({ id: 'ASSIGN_INSPECTOR_MESSAGE' })}</h2>
          <h3>{intl.formatMessage({ id: 'SHOULD_PERFORM_INSPECTION_MESSAGE' })}</h3>
        </div>

        <div className={classes.InspectorFilter}>
          <InspectorsFilter
            handleSelect={selectInspectorHandler}
            selectClassNamePrefix="assign-inspector-select"
          />
        </div>

        <div className={classes.Schedule}>
          <div>
            <label htmlFor="date">
              <FormattedMessage id="LABEL.DATE" />
            </label>
            <Select
              options={dates}
              id="date"
              isSearchable={false}
              placeholder={intl.formatMessage({ id: 'PLACEHOLDER.SELECT_DATE' })}
              name="date"
              classNamePrefix="assign-inspector-select"
            />
          </div>

          <div className={classes.twoColumns}>
            <div>
              <label htmlFor="start_time">
                <FormattedMessage id="LABEL.START_TIME" />
              </label>
              <MaskedInput
                type="text"
                name="start_time"
                id="start_time"
                placeholder={intl.formatMessage({ id: 'LABEL.START_TIME' })}
                required
                guide={false}
                mask={TIME_MASK}
              />
            </div>
            <div>
              <label htmlFor="end_time">
                <FormattedMessage id="LABEL.END_TIME" />
              </label>
              <MaskedInput
                type="text"
                name="end_time"
                id="end_time"
                placeholder={intl.formatMessage({ id: 'LABEL.END_TIME' })}
                required
                guide={false}
                mask={TIME_MASK}
              />
            </div>
          </div>
        </div>

        <div className={classes.Observations}>
          <label htmlFor="observations">
            <FormattedMessage id="LABEL.MESSAGE" />
          </label>
          <textarea 
            name="message" 
            id="observations" 
          />
        </div>

        <hr />

        {errorMessage && (
          <div className={classes.ErrorMessage}>
            {errorMessage}
          </div>
        )}

        <div className={classes.Actions}>
          <button
            type="button"
            className={classes.Cancel}
            onClick={cancelHandler}
            disabled={status === 'WORKING'}
          >
            <FormattedMessage id="BUTTON.CANCEL" />
          </button>
          <button
            type="submit"
            className={`${classes.Assign} ${status === 'WORKING' ? classes.isBusy : ''}`}
            disabled={status === 'WORKING'}
          >
            <FormattedMessage id="BUTTON.ASSIGN" />
          </button>
        </div>
      </div>
    </form>
  );
};

AssignInspector.propTypes = {
  assignDeviceToOrder: PropTypes.func.isRequired,
  cancelHandler: PropTypes.func.isRequired,
};

export default AssignInspector;
